import { useRef, useEffect, TouchEvent } from 'react';
import { Button, Flex, Text } from '@servicetitan/anvil2';
import SignatureCanvasLib from 'react-signature-canvas';

export interface SignatureCanvasProps {
    /** Callback when signature is created or cleared */
    onSignatureChange: (base64Signature: string | null) => void;
    /** Whether the signature canvas is disabled */
    disabled?: boolean;
    /** Custom width for the canvas */
    width?: number;
    /** Custom height for the canvas */
    height?: number;
    /** Label text for the signature area */
    label?: string;
    /** Clear button text */
    clearButtonText?: string;
}

export const SignatureCanvas = ({
    onSignatureChange,
    disabled = false,
    width = 620,
    height = 210,
    label = 'Sign here',
    clearButtonText = 'Clear',
}: SignatureCanvasProps) => {
    const signatureRef = useRef<SignatureCanvasLib>(null);

    const handleClear = () => {
        if (signatureRef.current) {
            signatureRef.current.clear();
            onSignatureChange(null);
        }
    };

    const handleSignature = () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            const base64Data = signatureRef.current.toDataURL();
            onSignatureChange(base64Data);
        } else {
            onSignatureChange(null);
        }
    };

    // Prevent touch events from bubbling up to prevent flyout closure
    const handleTouchMove = (e: TouchEvent) => {
        e.stopPropagation();
    };

    useEffect(() => {
        // Clear signature when component is disabled
        if (disabled && signatureRef.current) {
            signatureRef.current.clear();
            onSignatureChange(null);
        }
    }, [disabled, onSignatureChange]);

    return (
        <Flex direction="column" gap="3">
            <div
                style={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '16px',
                    backgroundColor: disabled ? '#f5f5f5' : 'white',
                    position: 'relative',
                }}
            >
                <SignatureCanvasLib
                    ref={signatureRef}
                    canvasProps={{
                        width,
                        height,
                        style: {
                            border: 'none',
                            cursor: disabled ? 'not-allowed' : 'crosshair',
                        },
                        onTouchMove: handleTouchMove,
                    }}
                    backgroundColor="transparent"
                    onEnd={handleSignature}
                />

                {/* Signature label */}
                <Text
                    variant="body"
                    color="secondary"
                    style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '16px',
                        pointerEvents: 'none',
                    }}
                >
                    {label}
                </Text>

                {/* Clear button */}
                <Button
                    size="small"
                    onClick={handleClear}
                    disabled={disabled}
                    style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                    }}
                >
                    {clearButtonText}
                </Button>
            </div>
        </Flex>
    );
};
